import type { UseAllergySelectionResult } from '@/features/rx-form/model/useAllergySelection';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import { useState } from 'react';

export interface AllergySelectorProps {
  selection: UseAllergySelectionResult;
}

export function AllergySelector({ selection }: AllergySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    addAllergy,
    addError,
    isSearchTextValid,
    pendingAddAllergenId,
    searchQuery,
    searchResults,
    searchText,
    searchTextError,
    setSearchText,
  } = selection;

  const isAdding = pendingAddAllergenId !== null;

  return (
    <div className="flex flex-col items-end gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        radius="pill"
        className="h-auto border-foreground/20 bg-transparent px-3 py-1.5 text-foreground shadow-none hover:bg-secondary/40 hover:text-foreground dark:bg-transparent dark:hover:bg-secondary/40"
        onClick={() => setIsOpen(true)}
        disabled={isAdding}
        aria-busy={isAdding}
      >
        {isAdding ? 'Adding…' : 'Add allergy'}
      </Button>

      <CommandDialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setSearchText('');
          }
        }}
        title="Add allergy"
        description="Search for an allergen to add to the patient’s record."
      >
        <CommandInput
          value={searchText}
          onValueChange={setSearchText}
          placeholder="Search allergies…"
          autoFocus
        />
        <CommandList>
          {searchText.trim().length === 0 ? (
            <CommandEmpty>Type an allergen name to search.</CommandEmpty>
          ) : !isSearchTextValid ? (
            <CommandEmpty>
              {searchTextError ?? 'Type at least 3 characters to search allergens.'}
            </CommandEmpty>
          ) : isAdding ? (
            <CommandEmpty>Adding allergy…</CommandEmpty>
          ) : searchQuery.isPending ? (
            <CommandEmpty>Searching…</CommandEmpty>
          ) : searchQuery.isError ? (
            <CommandEmpty>
              Search failed.
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void searchQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            </CommandEmpty>
          ) : searchResults.length === 0 ? (
            <CommandEmpty>No allergens found.</CommandEmpty>
          ) : (
            <CommandGroup heading="Results">
              {searchResults.map((r) => (
                <CommandItem
                  key={`${r.allergenId ?? 'none'}-${r.label}`}
                  value={`${r.label} ${r.allergenType}`}
                  disabled={r.allergenId === null || pendingAddAllergenId === r.allergenId}
                  onSelect={() => void addAllergy(r)}
                >
                  <div className="flex min-w-0 flex-col">
                    <div className="truncate">
                      {r.label}
                      {!r.isActive ? ' (inactive)' : ''}
                    </div>
                    <div className="text-muted-foreground truncate text-xs">{r.allergenType}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>

      {addError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not add allergy</AlertTitle>
          <AlertDescription>{addError}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
